import React, {Component, ReactElement, ReactNode} from 'react'
import { Box, Paper, Typography, Zoom } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning'
import {LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import styled from 'styled-components'
import PPCheckboxGroup from '../../../../components/shared/forms/PPCheckboxGroup'
import {Nullable} from '../../../../app/TypeHelpers'
import {AcceptedWarning, Warning} from './NatAcceptedWarning'
import {Colors} from '../../../../styledComponents/Constants'

interface DefaultProps {
    title: string
    description: string
}

interface Props extends DefaultProps {
    warnings: Warning[]
    changeAcceptedWarnings?: (acceptAll: boolean, acceptedWarning: AcceptedWarning[]) => void
    scrollBottom?: boolean
    doOpenAnimation?: boolean
}

interface States {
    warningItemList: WarningItem[]
}

interface WarningItem {
    messageId: string
    message: string
    checked: boolean
    highlighting: boolean
}

export default class NatAdminCommonAcceptedWarningComponent extends Component<Props, States> {
    static defaultProps: DefaultProps = {
        title: 'ご確認ください',
        description: '内容をご確認いただき、問題なければチェックしたうえで再度登録ボタンを押してください。',
    }

    constructor(props: Props) {
        super(props)

        this.state = {
            warningItemList: getAcceptedWarningInitialStatus(props.warnings),
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (JSON.stringify(prevProps.warnings) !== JSON.stringify(this.props.warnings)) {
            this.setState({warningItemList: getAcceptedWarningInitialStatus(this.props.warnings)})
        }
    }

    onChangeAcceptedWarning(messageId: string, index: number): void {
        const {warningItemList} = this.state

        // チェック状態を反転させる
        const afterWarningItemList: WarningItem[] = warningItemList
            .map((warningItem) => {
                return ({
                    messageId: warningItem.messageId,
                    message: warningItem.message,
                    checked: warningItem.checked,
                    highlighting: warningItem.highlighting,
                })
            })
        afterWarningItemList[index].checked = !afterWarningItemList[index].checked

        this.setState({
            warningItemList: afterWarningItemList,
        }, () => {
            if (this.props.changeAcceptedWarnings) {
                const acceptedWarningItemList: AcceptedWarning[] = afterWarningItemList
                    .filter((acceptedWarning) => acceptedWarning.checked)

                // チェックした件数
                const checkedCount: number = acceptedWarningItemList.length
                // チェックしたメッセージ
                const acceptedWarningList: AcceptedWarning[] = acceptedWarningItemList
                    .map((acceptedWarning) => {
                        return ({
                            messageId: acceptedWarning.messageId,
                        })
                    })

                this.props.changeAcceptedWarnings(
                    afterWarningItemList.length === checkedCount,
                    acceptedWarningList)
            }
        })
    }

    renderWarningArea(): ReactElement {
        const {title, description} = this.props
        const {warningItemList} = this.state

        return (
            <WarningArea>
                <WarningTitle>
                    <LeftRowFlexContainer>
                        <StyledWarningIcon/>
                        {title}
                    </LeftRowFlexContainer>
                </WarningTitle>
                <Typography variant="body2" component="p">
                    {description}
                </Typography>
                <Box height={14}/>
                {warningItemList.map((warning, index) => {
                    return (
                        <HighlightLabel highlightingFlag={warning.highlighting} key={index}>
                            <WarningPPCheckboxGroup
                                data={[{
                                    label: warning.message,
                                    checked: warning.checked,
                                }]}
                                onChange={() =>
                                    this.onChangeAcceptedWarning(warning.messageId, index)}
                            />
                        </HighlightLabel>
                    )
                })}
            </WarningArea>
        )
    }

    render(): Nullable<ReactElement> {
        const {doOpenAnimation} = this.props
        const {warningItemList} = this.state

        if (warningItemList.length === 0) {
            return null
        }
        if (doOpenAnimation === true) {
            return (
                <Zoom in={true}
                      onEntering={() => {
                          if (this.props.scrollBottom !== false) {
                              const element = document.documentElement
                              const bottom = element.scrollHeight - element.clientHeight
                              window.scroll(0, bottom)
                          }
                      }}
                >
                    {this.renderWarningArea()}
                </Zoom>
            )
        }
        return (this.renderWarningArea())
    }
}

function getAcceptedWarningInitialStatus(warnings: Warning[]): WarningItem[] {
    return warnings.map((warning) => {
        return ({
            messageId: `${warning.messageId}`,
            message: `${warning.message}`,
            checked: false,
            highlighting: (warning.highlighting === true),
        })
    })
}

const WarningArea = styled(Paper)`
  padding: 16px 16px 14px;
  margin: 5px 0px 5px;

  &.MuiPaper-root {
    background-color: rgba(245, 166, 35, 0.25);
  }

  & .MuiCheckbox-colorPrimary.Mui-checked {
    color: rgba(245, 166, 35, 1);
  }
`

const HighlightLabel = styled.div<{ highlightingFlag: boolean }>`
  color: ${props => props.highlightingFlag === true ? Colors.Alert : ''};
`

const StyledWarningIcon = styled(WarningIcon)`
  color: ${Colors.AccentDark};
  margin-right: 9px;
`

const WarningTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 9px;
`

const WarningPPCheckboxGroup = styled(PPCheckboxGroup)`
  & .MuiTypography-root {
    font-size: 14px;
  }
`
